// **React Imports
import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'

// **MUI Imports
import { ChevronRight, CorporateFare } from '@mui/icons-material'
import { Box, Menu, MenuItem, Typography, styled } from '@mui/material'

// **API Imports
import { switchTenant } from '../../../../axios/api/tenant'
import { DOMAIN, RESERVED_SUBDOMAINS } from '../../../../utils/constant'
import {
  getJWTToken,
  getLinkFromSubDomainLabel,
  getRememberMeFlag,
  getUserWorkspaceId,
  setCookie,
  showErrorToast
} from '../../../../utils/utils'
import { useDispatch } from 'react-redux'
import { setSelectedWorkspace } from '../../../../store/slice/workspace'

// **Styled Component
const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  '&.selected': {
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.primary.main,
    color: `${theme.palette.common.white} !important`,
    '&:hover': {
      backgroundColor: theme.palette.primary.main
    },
    '&:hover .MuiBox-root, &:hover .MuiBox-root svg': {
      color: `${theme.palette.common.white} !important`
    }
  },
  '&:hover .MuiBox-root, &:hover .MuiBox-root svg': {
    color: theme.palette.text.primary
  }
}))

// **static style

const styles = {
  px: 4,
  py: 1.75,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  '& svg': {
    fontSize: '1.5rem',
    color: 'text.secondary'
  }
}

const SwitchTenant = ({ data, selectedTenant, setTenantToken }) => {
  //Hooks
  const dispatch = useDispatch()

  //State
  const [anchorEl, setAnchorEl] = useState(null)

  //handlers
  const handleOpen = useCallback((e) => {
    setAnchorEl(e.currentTarget)
  }, [])

  const handleClose = useCallback((e) => {
    setTimeout(() => {
      setAnchorEl(null)
    })
  }, [])

  // variable
  const currentSubDomainLabel = useMemo(() => {
    if (window.location.hostname === 'localhost') {
      return 'localhost'
    }
    const hostnameParts = window.location.hostname.split('.')

    if (hostnameParts.length <= 2) {
      return null
    }
    return hostnameParts[0].toLowerCase()
  }, [])

  // handling token and flag using cookies
  const handleCookies = useCallback(() => {
    // variables
    const token = getJWTToken()
    const workspaceId = getUserWorkspaceId()
    const rememberMeFlag = getRememberMeFlag() || false

    if (token) {
      setCookie('user-token', token, {
        path: '/',
        domain: `.${DOMAIN}`,
        secure: window.location.origin.startsWith('https'),
        sameSite: 'Lax',
        maxAge: 30 * 24 * 60
      })
      setCookie('remember-me-flag', rememberMeFlag, {
        path: '/',
        domain: `.${DOMAIN}`,
        secure: window.location.origin.startsWith('https'),
        sameSite: 'Lax',
        maxAge: 30 * 24 * 60
      })
      setCookie('workspaceId', workspaceId, {
        path: '/',
        domain: `.${DOMAIN}`,
        secure: window.location.origin.startsWith('https'),
        sameSite: 'Lax',
        maxAge: 30 * 24 * 60
      })
    }
  }, [])

  const handleSwitchTenant = useCallback(
    async (id, subDomainLabel) => {
      const redirectionLink = getLinkFromSubDomainLabel(
        subDomainLabel ? subDomainLabel : RESERVED_SUBDOMAINS[1]
      )
      const response = await switchTenant(id)
      if (!response?.isError) {
        dispatch(setSelectedWorkspace(response?.workspaceId))
        setTenantToken(response?.token)
        handleClose()
        if (
          subDomainLabel &&
          subDomainLabel.toLowerCase() !== currentSubDomainLabel
        ) {
          handleCookies()
          window.location.href = redirectionLink
        }
      } else {
        showErrorToast({
          ...response,
          message: response?.message || 'Error occurred while switching tenant'
        })
      }
    },
    [
      dispatch,
      handleClose,
      handleCookies,
      setTenantToken,
      currentSubDomainLabel
    ]
  )

  return (
    <Fragment>
      <MenuItemStyled sx={{ p: 0 }} onClick={handleOpen}>
        <Box sx={styles}>
          <CorporateFare sx={{ mr: 1.5 }} />
          <Typography sx={{ mr: 1.5 }}>Switch Tenant</Typography>
          <ChevronRight />
        </Box>
      </MenuItemStyled>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleClose()}
        sx={{ '& .MuiMenu-paper': { minWidth: '230px', mt: 1.8 } }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left'
        }}
      >
        {data.map((item) => (
          <MenuItemStyled
            className={`${selectedTenant === item.id ? 'selected' : ''}`}
            key={item.id}
            sx={{ p: 0 }}
            onClick={() => handleSwitchTenant(item.id, item.subDomain)}
          >
            <Box sx={styles}>{item.name || 'Default '}</Box>
          </MenuItemStyled>
        ))}
      </Menu>
    </Fragment>
  )
}

export default memo(SwitchTenant)
